'use strict'

import {SysexManufacturers} from "./sysexmanufacturers.js";
import {FormattingUtils} from "./parsing_formatting/formattingutils.js";
import {Dbg} from "./util.js";
import {Constants} from "./constants.js";
const _ = require('lodash/core');


/** Base class for all types of sysex */
export class Sysex
{
  /** Create a new empty sysex.
   * @param {Uint8Array} rawData Raw sysex data. If not specified you must manually set rawData.
   */
  constructor(rawData = undefined)
  {
    //if (rawData) Sysex.sanityCheck(rawData);

    /** For all sysex types, rawData contains the binary MIDI data
     *  that can be sent to or received from a device,
     *  including start and end-of-sysex markers.
     Uint8Array
     */
    this.rawData = rawData;

    /** The name of the sysex. This is usually set on parsing
     *  if the sysex contains a name (such as the name of an
     *  individual patch), or otherwise may be the filename
     *  of the sysex file, or null if neither applies.
     */
    this.name = undefined;

    /** Manufacturer ID (1-3 bytes). Should be set for all sysex
     *  types when loaded.
     *  E.g. [0x43] for Yamaha.
     */
    this.manufacturerId = undefined;

    /** Name of manufacturer, if known, derived from Manufacturer
     *  ID on load.
     *  E.g. "Yamaha".
     */
    this.manufacturerName = undefined;

    /** Name of the device this sysex type is for, if known.
     *  Set when parsing a known sysex type.
     *  E.g. "DX7".
     */
    this.device = undefined; // E.g. "DX7"

    /** Sysex type for this device (many devices support several
     *  different types of sysex), if known.
     *  Set when parsing a known sysex type.
     *  E.g. "32-voice bank"
     */
    this.type = undefined;


    /** True for all known sysex types. If true, you can expect
     *  at least manufacturerName, device and type to be set.
     *  False for unknown/generic sysex data.
     */
    this.isKnownType = false;

    /** True for sysex types that can be parsed into
     *  into individual parameter data.
     *  If true, implements the following:
     *  - getParameterNames()
     *  - getParameterValue()
     *  - validate()
     *  - toObject()
     *  - fromObject()
     */
    this.canParse = false;

    // /** True in sysex types that contain sub-items
    //  *  that can be extracted and/or sent as individual sysex.
    //  *  If true, implements the following:
    //  *  - getSubItemCount()
    //  *  - getSubItemNames()
    //  *  - getSubItem()
    //  *  - setSubItem()
    //  */
    this.hasSubItems = false;

    /** True where the raw data contains several
        start- and end-of-sysex markers. */
    this.isMultiPart = false;

    if (rawData)
    {
      Sysex.sanityCheck(rawData);
      this.parseManufacturer();
      this.isMultiPart = Sysex.isMultiPartData(rawData);
    }
  }

  parseManufacturer()
  {
    if (!this.rawData) return;

    this.manufacturerId = SysexManufacturers.getId(this.rawData);
    this.manufacturerName = SysexManufacturers.getName(this.manufacturerId);
  }

  /** Tests the specified data for being valid SysEx data.
   *  Known sysex classes override this to test if the data
   *  is of that specific type.
   * @param {Uint8Array} rawData
   * @returns - True iff the data appears to be a valid sysex
   */
  static test(rawData) {
    try {
      this.sanityCheck(rawData);
    }
    catch (e) {
      Dbg.log(e.message);
      return false;
    }
    return true;
  }

  /** Basic checks to ensure the data is a (plausibly) valid sysex.
   * @param {Uint8Array} rawData
   * Throws Error if not valid.
   */
  static sanityCheck(rawData) {
    if (!rawData)
      throw new Error('SysEx has no data.');
    if (! rawData instanceof Uint8Array)
      throw new Error('SysEx data is not the right type.');
    if (rawData.byteLength <= 3)
      throw new Error('Not enough data for a valid SysEx.');
    if (rawData[0] != Constants.START_OF_SYSEX)
      throw new Error('Start-of-sysex marker not found in data.');
    if (rawData[rawData.byteLength-1] != Constants.END_OF_SYSEX)
      throw new Error('End-of-sysex marker not found in data.');
  }


  ///// For Sysexes that can be parsed

  /** Get the valid parameter names for this type of sysex
  *  @returns {string[]} Array of parameter names. */
  getParameterNames() {
    return undefined;
  }

  /** Returns the value for the specified parameter name. */
  getParameterValue(parameterName) {
    return undefined;
  }

  /** Validates the sysex data and parameter values.
   *  @returns {string[]} Any warnings or error messages. */
  validate() {
    return [];
  }

  /** Returns a plain object (mapping) of parameter name => value. */
  toObject() {
    return undefined; // returns object with parsed parameter values
  }

  /** Initializes from an object (mapping) of parameter name => value. */
  static fromObject(parameterValues) {
    return undefined;
  }


  ///// For Sysexes that have sub-items

  /** Returns the number of sub-items in this sysex. */
  getSubItemCount() {
    return 0;
  }

  /** Returns the names of sub-items in this sysex.
   *  @returns {string[]} Array of sub-item names (if applicable) */
  getSubItemNames() {
    return [];
  }

  /** Returns the nth sub-item (0-based) as a Sysex */
  getSubItem(number) {
    return undefined;
  }

  /** Replaces the nth sub-item (0-based) with the specified Sysex */
  setSubItem(number, sysex) {
    throw new Error("Sysex does not have sub-items");
  }


  ///// For multi-part Sysexes (such as MIDI Sample Dumps)

  static isMultiPartData(rawData) {
    let firstIndex = rawData.indexOf(Constants.START_OF_SYSEX);
    if (firstIndex === -1) return false;
    let secondIndex = rawData.indexOf(Constants.START_OF_SYSEX, firstIndex+1);
    if (secondIndex === -1) return false;
    return true;
  }

  /** Returns the next part (next complete sysex) in
   *  multi-part data, starting at the specified index.
   *  Returns undefined if no more data or startIndex does not
   *  point to the start of a sysex part.
   * @param {int} startIndex Byte index where the requested part starts.
   */
  getNextPart(startIndex)
  {
    if (this.rawData[startIndex] !== Constants.START_OF_SYSEX) {
      return undefined;
    }
    let endIndex = this.rawData.indexOf(Constants.END_OF_SYSEX, startIndex);
    return new Uint8Array(this.rawData.buffer,
      this.rawData.byteOffset + startIndex,
      endIndex - startIndex + 1);
  }

  /** Returns array of Uint8Array with the individual
      raw data for each part of this sysex. */
  getParts() {
    let offset = 0;
    let parts = [];
    while (offset < this.rawData.byteLength) {
      let part = this.getNextPart(offset);
      parts.push(part);
      offset += part.byteLength;
    }
    return parts;
  }
}



// WEBPACK FOOTER //
// ./src/sysex.js